import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, clearError } from '../../../Store/actions/userActions';
import SocialLogin from '../../../components/SocialLogin/SocialLogin';
import ApiService from '../../../services/apiService';
import { parseAuthError } from '../../../services/authService';
import AppleAuthService from '../../../services/appleAuthService';
import PasswordStrengthIndicator from '../../../components/ui/PasswordStrengthIndicator';
import RateLimitingWrapper from '../../../components/ui/RateLimitingWrapper';
import RegistrationErrorDisplay from '../../../components/ui/RegistrationErrorDisplay';
import { validateForm, sanitizeFormData, validateSubmissionRate, validateName, validateEmail, validatePassword, validateDateOfBirth } from '../../../utils/validationUtils';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { loading, error, fieldErrors, isAuthenticated, user } = useSelector(state => state.user);
  const messagesEndRef = useRef(null);

  // Get referral code from URL
  const referralCode = searchParams.get('ref');

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    password: '',
    referralCode: referralCode || ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [userResponses, setUserResponses] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [lastSubmission, setLastSubmission] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Clear any previous errors when component mounts
  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [dispatch, error]);

  const conversationSteps = [
    {
      message: referralCode
        ? "Welcome to Hebrew Hearts! 🎉 You've been invited by a friend! I'm here to help you find your perfect match. Let's start by getting to know you a bit better."
        : "Welcome to Hebrew Hearts! I'm here to help you find your perfect match. Let's start by getting to know you a bit better.",
      showInput: false,
      autoAdvance: true
    },
    {
      message: "How would you like to create your account? You can either tell me your details or sign up quickly with Google, Facebook, or Apple.",
      showInput: false,
      showSocialLogin: true
    },
    {
      message: "First, what's your first name?",
      showInput: true,
      inputType: 'firstName',
      placeholder: 'Type your first name...',
      validation: (value) => {
        const result = validateName(value, 'First name');
        return result.isValid ? null : result.message;
      }
    },
    {
      message: "Great! Now what's your last name?",
      showInput: true,
      inputType: 'lastName',
      placeholder: 'Type your last name...',
      validation: (value) => {
        const result = validateName(value, 'Last name');
        return result.isValid ? null : result.message;
      }
    },
    {
      message: "When's your birthday? Hebrew Hearts is for adults 18 and over.",
      showInput: true,
      inputType: 'dateOfBirth',
      htmlType: 'date',
      placeholder: 'Select your date of birth...',
      validation: (value) => {
        const result = validateDateOfBirth(value);
        return result.isValid ? null : result.message;
      }
    },
    {
      message: "Great! Now, please enter your email address.",
      showInput: true,
      inputType: 'email',
      placeholder: 'Type your email...',
      validation: (value) => {
        const result = validateEmail(value);
        return result.isValid ? null : result.message;
      }
    },
    {
      message: "And finally, create a password for your account.",
      showInput: true,
      inputType: 'password',
      placeholder: 'Type your password...',
      validation: (value) => {
        const result = validatePassword(value);
        return result.isValid ? null : result.message;
      }
    },
    {
      message: "Just a moment while I process your information...",
      showInput: false,
      autoAdvance: true
    },
    {
      message: `Okay, I have your details. Please confirm: Name: ${formData.firstName} ${formData.lastName}, Email: ${formData.email}`,
      showInput: false
    }
  ];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Check if user needs OTP verification
      if (user && !user.isVerified) {
        navigate('/verify-otp', { state: { email: formData.email } });
      } else {
        navigate('/pre-onboard');
      }
    }
  }, [isAuthenticated, user, navigate, formData.email]);

  // Auto-advance for steps that don't require user input
  useEffect(() => {
    const currentStepData = conversationSteps[currentStep];
    if (currentStepData && currentStepData.autoAdvance) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 1500); // Wait 1.5 seconds before auto-advancing
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, conversationSteps]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }, [currentStep, userResponses, isProcessing, showConfirmation]);

  // Listen for manual signup message from social login component
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'MANUAL_SIGNUP') {
        setCurrentStep(prev => prev + 1);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const validateField = (field, value) => {
    const currentStepData = conversationSteps[currentStep];
    if (currentStepData && currentStepData.validation) {
return currentStepData.validation(value);
    }
    return null;
  };

  const getFieldError = (field) => {
    // Check for server-side field errors first
    if (fieldErrors[field]) {
      return fieldErrors[field];
    }
    // Then check for client-side validation errors
    return validationErrors[field];
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    
    // Clear field errors from server when user starts typing
    if (fieldErrors[field]) {
      dispatch(clearError());
    }
  };

  const handleInputSubmit = (field) => {
    const value = formData[field];
    const validationError = validateField(field, value);
    
    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: validationError
      }));
      return;
    }

    if (value && value.trim()) {
      // Update or add user response to the conversation
      setUserResponses(prev => {
        const existingIndex = prev.findIndex(response => response.step === currentStep);
        if (existingIndex !== -1) {
          // Update existing response
          const updated = [...prev];
          updated[existingIndex] = { step: currentStep, field, value: value };
          return updated;
        } else {
          // Add new response
          return [...prev, { step: currentStep, field, value: value }];
        }
      });
      
      if (currentStep < conversationSteps.length - 2) {
        setCurrentStep(prev => prev + 1);
      } else if (currentStep === conversationSteps.length - 2) {
        setIsProcessing(true);
        // Simulate processing time
        setTimeout(() => {
          setIsProcessing(false);
          setCurrentStep(prev => prev + 1);
          setShowConfirmation(true);
        }, 2000);
      }
    }
  };

  const handleKeyPress = (e, field) => {
    if (e.key === 'Enter') {
      handleInputSubmit(field);
    }
  };

  const handleConfirmSignUp = async () => {
    console.log('handleConfirmSignUp called');
    console.log('Form data:', formData);
    console.log('isPasswordValid:', isPasswordValid);
    console.log('loading:', loading);
    console.log('isProcessing:', isProcessing);
    console.log('isRateLimited:', isRateLimited);

    // Check rate limiting
    const rateLimitCheck = validateSubmissionRate(lastSubmission, 5000);
    if (!rateLimitCheck.isValid) {
      console.log('Rate limited!');
      setIsRateLimited(true);
      return;
    }

    // Validate form data
    const sanitizedData = sanitizeFormData(formData);
    console.log('Sanitized data:', sanitizedData);
    const validation = validateForm(sanitizedData);
    console.log('Validation result:', validation);

    if (!validation.isValid) {
      console.log('Validation errors:', validation.errors);
      setValidationErrors(validation.errors);
      return;
    }

    // Check password strength
    if (!isPasswordValid) {
      console.log('Password not valid!');
      console.log('Password strength:', passwordStrength);
      console.log('Form password:', formData.password);

      // Get detailed password validation
      const passwordValidation = validatePassword(formData.password);
      console.log('Password validation details:', passwordValidation);

      // Only show error if password is actually invalid
      if (!passwordValidation.isValid) {
        setValidationErrors({ password: passwordValidation.message });
        alert(`Password validation failed: ${passwordValidation.message}`);
        return;
      } else {
        // Password is actually valid, so clear any errors and continue
        console.log('Password validation says valid, but isPasswordValid is false. Continuing anyway.');
        setValidationErrors({});
      }
    }

    // Clear any validation errors before proceeding
    setValidationErrors({});

    try {
      setIsProcessing(true);
      setLastSubmission(Date.now());

      console.log('Dispatching registerUser with:', sanitizedData);
      const result = await dispatch(registerUser(sanitizedData));
      console.log('Registration result:', result);

      // After successful registration, redirect to OTP verification
      navigate('/verify-otp', { state: { email: sanitizedData.email } });
    } catch (error) {
      console.error('Registration failed:', error);
      // Error is handled by the reducer
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSocialLogin = async (provider, userData) => {
    try {
      // Optional: reflect in UI
      const fullName = userData.name || userData.displayName || '';
      const nameParts = fullName.split(' ');
      setFormData({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: userData.email || '',
        password: ''
      });
      setUserResponses(prev => ([...prev, { step: 1, field: 'social', value: `Signed up with ${provider}` }]));

      // Create/login user via backend
      let payload;
      let endpoint;

      if (provider === 'apple') {
        // Apple authentication payload
        payload = {
          userData: {
            email: userData.email,
            name: fullName,
            photoURL: userData.photoURL,
            appleUid: userData.uid,
            emailVerified: userData.emailVerified
          },
          appleToken: userData.token,
          appleCode: userData.code
        };
        endpoint = '/auth/apple';
      } else {
        // Google: send verified Firebase ID token only
        const firebaseToken = userData.firebaseToken || userData.token || userData.idToken;
        if (!firebaseToken) {
          throw new Error('Failed to obtain Firebase ID token');
        }
        payload = { firebaseToken };
        endpoint = '/auth/google';
      }

      const response = await ApiService.fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await ApiService.handleResponse(response);

      if (data?.token && data?.user) {
        localStorage.setItem('token', data.token);
        dispatch({
          type: 'USER_REGISTER_SUCCESS',
          payload: {
            token: data.token,
            user: data.user
          }
        });
        if (window.toast) window.toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-up successful!`);
        navigate('/pre-onboard');
      } else {
        throw new Error(`${provider.charAt(0).toUpperCase() + provider.slice(1)} authentication failed`);
      }
    } catch (error) {
      const parsed = parseAuthError(error);
      if (window.toast) window.toast.error(parsed.message);
      console.error('Social login error:', error);
    }
  };

  const handleEditStep = (stepIndex) => {
    setEditingStep(stepIndex);
    setCurrentStep(stepIndex);
    
    // Auto-fill all inputs with existing values
    const existingResponses = userResponses.filter(response => response.step !== stepIndex);
    existingResponses.forEach(response => {
      setFormData(prev => ({
        ...prev,
        [response.field]: response.value
      }));
    });
  };

  const handleBackToCurrent = () => {
    setEditingStep(null);
    // Find the last step that was completed
    const lastCompletedStep = conversationSteps.findIndex((step, index) => {
      if (index === 0) return false; // Skip welcome message
      if (step.showInput) {
        return !formData[step.inputType] || !formData[step.inputType].trim();
      }
      return false;
    });
    setCurrentStep(lastCompletedStep === -1 ? conversationSteps.length - 1 : lastCompletedStep);
  };

  const currentStepData = conversationSteps[currentStep];

  return (
    <div className="register-container">
      <div className="layout-container">
        <div className="main-content">
          <div className="conversation-container">
            {/* Show error message if registration failed */}
            {error && (
              <div className="error-message">
                <p>{error}</p>
              </div>
            )}

            {/* Render conversation messages and user responses */}
            {conversationSteps.slice(0, currentStep + 1).map((step, index) => {
              // Find user response for this step
              const userResponse = userResponses.find(response => response.step === index);
              
              return (
                <React.Fragment key={index}>
                  {/* AI Message */}
                  <div className="message-container">
                    <div className="avatar">
                      <div 
                        className="avatar-image"
                        style={{
                          backgroundImage: `url("https://lh3.googleusercontent.com/aida-public/AB6AXuDvkfE-2atWJ0Qu6wziNmJoqC42Jdya4eb72Ix2cQEKxzh6M-h5uAq8A5Ez1yp7dczSgnZ77SyjRaE1NN7dVZybVtKwyvnzmTKJSMNC4mFNjSYbv0UtS4tYVTd4TvK3J0um8tduZmXAoExpOkU-2eESmqHGqeYY5gpYzCEW96pwtvAEtiKUrSqD-V-owS9QcK6ePFtq5IZe5wIjQLJQJl0xb4FNBzWyYgEPLCzPJgERekvmK8HiQNMQMLgMYo5zIK9i3U25uhute-U")`
                        }}
                      />
                    </div>
                    <div className="message-content">
                      <p className="message-sender">MatchAI</p>
                      <p className="message-text">{step.message}</p>
                    </div>
                  </div>
                  
                  {/* User Response */}
                  {userResponse && (
                    <div className="message-container user-message">
                      <div className="message-content user-message-content">
                        <p className="message-text user-message-text">
                          {userResponse.field === 'password' ? 
                            (showPassword ? userResponse.value : '•'.repeat(userResponse.value.length)) : 
                            userResponse.value
                          }
                        </p>
                        {userResponse.field === 'password' && (
                          <button 
                            className="password-toggle-button"
                            onClick={() => setShowPassword(!showPassword)}
                            title={showPassword ? "Hide password" : "Show password"}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              {showPassword ? (
                                <>
                                  <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </>
                              ) : (
                                <>
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2 16 2 12C2 8 7 4 12 4C16 4 20 8 20 12C20 16 17.94 17.94 17.94 17.94Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4C16 4 20 8 20 12C20 16 17.94 17.94 17.94 17.94Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </>
                              )}
                            </svg>
                          </button>
                        )}
                        <button 
                          className="edit-icon-button"
                          onClick={() => handleEditStep(index)}
                          title={`Edit ${userResponse.field}`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.5 2.50023C18.8978 2.10297 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10297 21.5 2.50023C21.8971 2.89792 22.1212 3.43767 22.1212 4.00023C22.1212 4.56279 21.8971 5.10254 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* Show input field for current step if needed */}
            {currentStepData.showInput && (
              <div className="input-container">
                <div className="input-wrapper">
                  <label className="input-label">
                    <input
                      type={currentStepData.inputType === 'password' ? 'password' : (currentStepData.htmlType || 'text')}
                      max={currentStepData.htmlType === 'date' ? new Date().toISOString().split('T')[0] : undefined}
                      placeholder={currentStepData.placeholder}
                      className={`form-input ${getFieldError(currentStepData.inputType) ? 'error' : ''}`}
                      value={formData[currentStepData.inputType] || ''}
                      onChange={(e) => handleInputChange(currentStepData.inputType, e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, currentStepData.inputType)}
                    />
                  </label>
                  <button
                    className="submit-button"
                    onClick={() => handleInputSubmit(currentStepData.inputType)}
                    disabled={!formData[currentStepData.inputType] || !formData[currentStepData.inputType].trim()}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                {getFieldError(currentStepData.inputType) && (
                  <div className="validation-error">
                    {getFieldError(currentStepData.inputType)}
                  </div>
                )}
                
                {/* Password Strength Indicator */}
                {currentStepData.inputType === 'password' && formData.password && (
                  <PasswordStrengthIndicator
                    password={formData.password}
                    onStrengthChange={(strength, isValid) => {
                      setPasswordStrength(strength);
                      setIsPasswordValid(isValid);
                    }}
                  />
                )}
                {/* Show back button when editing */}
                {editingStep !== null && (
                  <button 
                    className="back-button"
                    onClick={handleBackToCurrent}
                  >
                    Back to current
                  </button>
                )}
              </div>
            )}

            {/* Show social login options */}
            {currentStepData.showSocialLogin && (
              <div className="social-login-step">
                <SocialLogin 
                  onSocialLogin={handleSocialLogin}
                  isLoading={loading}
                />
              </div>
            )}

            {/* Show confirmation button on last step */}
            {(showConfirmation || currentStep === conversationSteps.length - 1) && (
              <RateLimitingWrapper
                cooldownMs={5000}
                maxAttempts={3}
                timeWindow={300000}
                onRateLimitExceeded={(status) => {
                  console.log('Rate limit exceeded:', status);
                }}
              >
                <div className="confirmation-container">
                  <button
                    className="confirm-button"
                    onClick={handleConfirmSignUp}
                    disabled={loading || isProcessing || isRateLimited}
                  >
                    <span>
                      {loading || isProcessing ? 'Signing up...' : 
                       isRateLimited ? 'Please wait...' : 
                       'Confirm and Sign Up'}
                    </span>
                  </button>
                </div>
              </RateLimitingWrapper>
            )}

            {/* Display registration errors */}
            <RegistrationErrorDisplay
              error={error}
              fieldErrors={{...fieldErrors, ...validationErrors}}
            />

            {/* Display validation errors prominently */}
            {Object.keys(validationErrors).length > 0 && (
              <div style={{
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                padding: '12px',
                borderRadius: '8px',
                marginTop: '12px',
                color: '#c00'
              }}>
                {Object.entries(validationErrors).map(([field, message]) => (
                  <div key={field} style={{ marginBottom: '4px' }}>
                    <strong>{field}:</strong> {message}
                  </div>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
